import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const LineGroupVerified = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const groupName = searchParams.get('group');
  const success = searchParams.get('success');

  useEffect(() => {
    // Auto redirect to login after 5 seconds
    const timer = setTimeout(() => {
      navigate('/login');
    }, 5000);

    return () => clearTimeout(timer);
  }, [navigate]);

  if (success !== 'true') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Verification Failed
            </h1>
            
            <p className="text-gray-600 mb-6">
              The verification link is invalid or has expired.
              Please request a new verification email from your LINE group.
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">
                <strong>Tip:</strong> Send "resend" in your LINE group to get a new verification email.
              </p>
            </div>
            
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          {/* Success Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            🎉 Registration Confirmed!
          </h1>
          
          {/* Group Name */}
          {groupName && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">LINE Group</p>
              <p className="text-lg font-semibold text-blue-900">{decodeURIComponent(groupName)}</p>
            </div>
          )}
          
          {/* Success Message */}
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              Your LINE group has been successfully verified and is now active for conversation logging.
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
              <p className="font-semibold text-green-900 mb-2">✅ What's Next:</p>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Check your LINE group for confirmation message</li>
                <li>• All messages will be logged to notes</li>
                <li>• Images and files are saved as attachments</li>
                <li>• A new note is created each day</li>
              </ul>
            </div>
          </div>
          
          {/* CTA Button */}
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            Go to Dashboard
          </button>
          
          {/* Auto Redirect Notice */}
          <p className="text-sm text-gray-500 mt-4">
            Redirecting to login in 5 seconds...
          </p>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Powered by{' '}
            <a 
              href="https://app.flowai.co.th" 
              className="text-blue-600 hover:text-blue-700 font-semibold"
              target="_blank"
              rel="noopener noreferrer"
            >
              FlowAI CRM
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LineGroupVerified;

